import React, { useState, useRef } from "react";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface UploadOfferLetterProps {
  candidateData: {
    name: string;
    position: string;
  };
  onUpload: (file: File) => void;
  onCancel: () => void;
  onSendToCandidate: (file: File) => void;
  isUploading: boolean;
}

export default function UploadOfferLetter({
  candidateData,
  onUpload,
  onCancel,
  onSendToCandidate,
  isUploading,
}: UploadOfferLetterProps) {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation("job-details");

  const handleFileSelect = (file: File) => {
    if (file && file.type === "application/pdf") {
      setUploadedFile(file);
      // Simulate upload progress
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 10;
        setUploadProgress(progress);
        if (progress >= 100) {
          clearInterval(progressInterval);
        }
      }, 100);
    } else {
      alert(t("jobDetails.finalDecision.upload.pdfOnly"));
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSendToCandidate = () => {
    if (uploadedFile) {
      onSendToCandidate(uploadedFile);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="upload-offer-letter">
      <div className="upload-offer-letter__header">
        <h3 className="upload-offer-letter__title">{t("jobDetails.finalDecision.upload.title")}</h3>
      </div>

      <p className="upload-offer-letter__description">
        {t("jobDetails.finalDecision.upload.description")}
      </p>

      {!uploadedFile ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`upload-offer-letter__drop-zone ${isDragOver ? "upload-offer-letter__drop-zone--drag-over" : ""}`}
          onClick={handleUploadClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileInputChange}
            className="upload-offer-letter__file-input"
          />

          <div className="upload-offer-letter__upload-icon">
            <i className="fa fa-upload"></i>
          </div>

          <h4 className="upload-offer-letter__upload-title">
            <span>{t("jobDetails.finalDecision.upload.clickToUpload")}</span> {t("jobDetails.finalDecision.upload.orDragDrop")}
          </h4>
          <p className="upload-offer-letter__file-size-info">{t("jobDetails.finalDecision.upload.maxFileSize")}</p>
          <p className="upload-offer-letter__file-type-info">{t("jobDetails.finalDecision.upload.pdfOnly")}</p>
        </div>
      ) : (
        <div className="upload-offer-letter__file-preview">
          <div className="upload-offer-letter__file-info">
            <div className="upload-offer-letter__file-details">
              <div className="upload-offer-letter__file-icon">
                <i className="fa fa-file-pdf-o"></i>
              </div>
              <div>
                <p className="upload-offer-letter__file-name">{uploadedFile.name}</p>
                <p className="upload-offer-letter__file-size">{formatFileSize(uploadedFile.size)}</p>
              </div>
            </div>
            <button onClick={handleRemoveFile} className="upload-offer-letter__remove-btn">
              <i className="fa fa-trash"></i>
            </button>
          </div>

          {uploadProgress < 100 && (
            <div className="upload-offer-letter__progress">
              <div className="upload-offer-letter__progress-info">
                <span className="upload-offer-letter__progress-text">{t("jobDetails.finalDecision.upload.uploading")}</span>
                <span className="upload-offer-letter__progress-text">{uploadProgress}%</span>
              </div>
              <div className="upload-offer-letter__progress-bar">
                <div className="upload-offer-letter__progress-fill" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="upload-offer-letter__actions">
        <button type="button" onClick={onCancel} className="btn-cancel">
          {t("jobDetails.finalDecision.upload.cancel")}
        </button>

        <button
          type="button"
          onClick={handleSendToCandidate}
          disabled={!uploadedFile || uploadProgress < 100 || isUploading}
          className="btn-preview"
        >
          {isUploading ? t("jobDetails.finalDecision.upload.sending") : t("jobDetails.finalDecision.upload.sendToCandidate")}
        </button>
      </div>
    </div>
  );
}
